import { useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'

import { ApiError } from '../api/client'
import { endpoints } from '../api/endpoints'
import { attributionForSignup, forgetAttribution } from '../attribution'
import { useSession } from '../auth/context'
import { detectTimezone } from '../businessTypes'
import { BusinessTypePicker, type TypeChoice } from '../components/BusinessTypePicker'
import { PasswordField } from '../components/PasswordField'
import { LegalLinks } from './LegalPages'
import { errorMessage, t } from '../strings'

function useSubmit() {
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    setErrorCode(null)
    try {
      await action()
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'unknown'
      setErrorCode(code)
      setError(errorMessage(code))
    } finally {
      setBusy(false)
    }
  }
  return { error, errorCode, busy, run }
}

/**
 * The frame of the signed-out pages, in the manner of gammapos.id: the logo on white, the page's name on
 * navy with the swept corner, then the form as open text (no card). On a wide screen the navy panel stands
 * beside the form.
 */
function AuthShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="auth">
      <div className="auth-top">
        <img src="/logo-mark.webp" alt="" width="36" height="38" />
        <span className="auth-name">{t.appName}</span>
      </div>
      <div className="auth-head">
        <h1>{title}</h1>
        <p>{t.authPitch}</p>
      </div>
      <div className="auth-body">
        {children}
        <LegalLinks />
      </div>
    </main>
  )
}

export function LoginPage() {
  const session = useSession()
  const navigate = useNavigate()
  const { error, busy, run } = useSubmit()
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  // Set by the forgotten-password page once the new password is saved.
  const justReset = (useLocation().state as { passwordReset?: boolean } | null)?.passwordReset === true
  // Set by the reopen page once the closed account is open again.
  const justReopened = (useLocation().state as { accountReopened?: boolean } | null)?.accountReopened === true

  function submit(e: FormEvent) {
    e.preventDefault()
    void run(async () => {
      session.signedIn(await endpoints.login(phone, password))
      await navigate('/')
    })
  }

  return (
    <AuthShell title={t.loginTitle}>
      {justReset && (
        <p className="notice" role="status">
          {t.forgotDone}
        </p>
      )}
      {justReopened && (
        <p className="notice" role="status">
          {t.reopenDone}
        </p>
      )}
      <form onSubmit={submit}>
        <label>
          {t.phone}
          <input inputMode="tel" autoComplete="tel" maxLength={20} value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </label>
        <PasswordField label={t.password} autoComplete="current-password" value={password} onChange={setPassword} />
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy}>
          {t.loginButton}
        </button>
      </form>
      <p>
        <Link to="/lupa-kata-sandi">{t.forgotPassword}</Link>
      </p>
      <Link to="/daftar">{t.noAccount}</Link>
    </AuthShell>
  )
}

/** A forgotten password in three steps: the phone number, the code that arrives by SMS, the new password (typed twice). */
export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const { error, busy, run } = useSubmit()
  const [step, setStep] = useState<'phone' | 'code' | 'password'>('phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [proof, setProof] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [confirmTouched, setConfirmTouched] = useState(false)
  const mismatch = confirmTouched && confirm !== next
  const [resendAt, setResendAt] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (step !== 'code') return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [step])
  const wait = Math.max(0, Math.ceil((resendAt - now) / 1000))

  async function sendCode() {
    // The answer is the same whether or not the number has an account: the page never says which.
    const sent = await endpoints.otpRequest(phone, undefined, 'RESET')
    setCode('')
    setNow(Date.now())
    setResendAt(Date.now() + sent.resend_after * 1000)
    setStep('code')
  }

  function submitPhone(e: FormEvent) {
    e.preventDefault()
    void run(sendCode)
  }

  function submitCode(e: FormEvent) {
    e.preventDefault()
    void run(async () => {
      const verified = await endpoints.otpVerify(phone, code.trim(), 'RESET')
      setProof(verified.phone_proof)
      setStep('password')
    })
  }

  function submitPassword(e: FormEvent) {
    e.preventDefault()
    setConfirmTouched(true)
    if (confirm !== next) return
    void run(async () => {
      await endpoints.resetPassword(phone, proof, next)
      await navigate('/masuk', { state: { passwordReset: true } })
    })
  }

  const errorLine = error && (
    <p className="error" role="alert">
      {error}
    </p>
  )

  if (step === 'password') {
    return (
      <AuthShell title={t.forgotNewTitle}>
        <form onSubmit={submitPassword}>
          <PasswordField label={t.newPasswordLabel} autoComplete="new-password" minLength={8} value={next} onChange={setNext} />
          <PasswordField
            label={t.confirmPassword}
            autoComplete="new-password"
            value={confirm}
            onChange={(v) => {
              setConfirm(v)
              if (v.length >= next.length) setConfirmTouched(true)
            }}
            error={mismatch ? t.passwordMismatch : null}
          />
          {errorLine}
          <button type="submit" disabled={busy}>
            {t.forgotSave}
          </button>
        </form>
      </AuthShell>
    )
  }

  if (step === 'code') {
    return (
      <AuthShell title={t.otpTitle}>
        <form onSubmit={submitCode}>
          <p>{t.forgotCodeSent(phone)}</p>
          <label>
            {t.otpCode}
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]{6}"
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </label>
          {errorLine}
          <button type="submit" disabled={busy || code.length !== 6}>
            {t.forgotVerify}
          </button>
          <button type="button" className="link" disabled={busy || wait > 0} onClick={() => void run(sendCode)}>
            {wait > 0 ? t.otpResendIn(wait) : t.otpResend}
          </button>
          <button type="button" className="link" onClick={() => setStep('phone')}>
            {t.forgotChangeNumber}
          </button>
        </form>
      </AuthShell>
    )
  }

  return (
    <AuthShell title={t.forgotTitle}>
      <form onSubmit={submitPhone}>
        <p>{t.forgotBody}</p>
        <label>
          {t.phone}
          <input inputMode="tel" autoComplete="tel" maxLength={20} autoFocus value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </label>
        {errorLine}
        <button type="submit" disabled={busy}>
          {t.forgotSend}
        </button>
      </form>
      <Link to="/masuk">{t.backToLogin}</Link>
    </AuthShell>
  )
}

/** A closed account opened again, in three steps: the phone number, the code that arrives by SMS, done (ADR-0025). */
export function ReopenAccountPage() {
  const navigate = useNavigate()
  const { error, busy, run } = useSubmit()
  const [step, setStep] = useState<'phone' | 'code'>('phone')
  const [phone, setPhone] = useState((useLocation().state as { phone?: string } | null)?.phone ?? '')
  const [code, setCode] = useState('')
  const [resendAt, setResendAt] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (step !== 'code') return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [step])
  const wait = Math.max(0, Math.ceil((resendAt - now) / 1000))

  async function sendCode() {
    // The answer is the same whether or not the number can be reopened: the page never says which.
    const sent = await endpoints.otpRequest(phone, undefined, 'REOPEN')
    setCode('')
    setNow(Date.now())
    setResendAt(Date.now() + sent.resend_after * 1000)
    setStep('code')
  }

  function submitPhone(e: FormEvent) {
    e.preventDefault()
    void run(sendCode)
  }

  function submitCode(e: FormEvent) {
    e.preventDefault()
    void run(async () => {
      const verified = await endpoints.otpVerify(phone, code.trim(), 'REOPEN')
      await endpoints.reopenAccount(phone, verified.phone_proof)
      await navigate('/masuk', { state: { accountReopened: true } })
    })
  }

  const errorLine = error && (
    <p className="error" role="alert">
      {error}
    </p>
  )

  if (step === 'code') {
    return (
      <AuthShell title={t.otpTitle}>
        <form onSubmit={submitCode}>
          <p>{t.reopenCodeSent(phone)}</p>
          <label>
            {t.otpCode}
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]{6}"
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </label>
          {errorLine}
          <button type="submit" disabled={busy || code.length !== 6}>
            {t.reopenVerify}
          </button>
          <button type="button" className="link" disabled={busy || wait > 0} onClick={() => void run(sendCode)}>
            {wait > 0 ? t.otpResendIn(wait) : t.otpResend}
          </button>
          <button type="button" className="link" onClick={() => setStep('phone')}>
            {t.forgotChangeNumber}
          </button>
        </form>
      </AuthShell>
    )
  }

  return (
    <AuthShell title={t.reopenTitle}>
      <form onSubmit={submitPhone}>
        <p>{t.reopenBody}</p>
        <label>
          {t.phone}
          <input inputMode="tel" autoComplete="tel" maxLength={20} autoFocus value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </label>
        {errorLine}
        <button type="submit" disabled={busy}>
          {t.reopenSend}
        </button>
      </form>
      <Link to="/masuk">{t.backToLogin}</Link>
    </AuthShell>
  )
}

export function SignupPage() {
  const session = useSession()
  const navigate = useNavigate()
  const { error, errorCode, busy, run } = useSubmit()
  const [f, setF] = useState({
    invite_code: '',
    phone: '',
    password: '',
    name: '',
    business_name: '',
    outlet_name: '',
    outlet_code: '',
  })
  const [type, setType] = useState<TypeChoice>({ type: '', subtype: '', note: '' })
  const [typeMissing, setTypeMissing] = useState(false)
  const [confirm, setConfirm] = useState('')
  const [confirmTouched, setConfirmTouched] = useState(false)
  const mismatch = confirmTouched && confirm !== f.password
  // Phone verification: set once the server has sent a code; `proof` once the code was right (valid for that phone only).
  const [otp, setOtp] = useState<{ resendAt: number } | null>(null)
  const [code, setCode] = useState('')
  const [proof, setProof] = useState<{ phone: string; value: string } | null>(null)
  const [now, setNow] = useState(() => Date.now())
  // Shown until the server says otherwise: an invite code is asked for while the server still wants one.
  const [inviteRequired, setInviteRequired] = useState(true)
  useEffect(() => {
    void endpoints.signupConfig().then((c) => setInviteRequired(c.invite_required), () => undefined)
  }, [])
  useEffect(() => {
    if (!otp) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [otp])
  const wait = otp ? Math.max(0, Math.ceil((otp.resendAt - now) / 1000)) : 0
  const field = (k: keyof typeof f) => ({
    value: f[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value }),
    required: true,
  })

  async function finish(phoneProof?: string) {
    const kind = type.type
    if (kind === '') return // submit() has already asked for one before any code was sent
    const invite_code = inviteRequired ? f.invite_code : undefined
    const attribution = attributionForSignup()
    session.signedIn(
      await endpoints.signup({
        ...f,
        invite_code,
        outlet_code: f.outlet_code.toUpperCase(),
        // The time zone is not asked: the browser's own says it.
        timezone: detectTimezone(),
        business_type: kind,
        business_subtype: type.subtype || undefined,
        business_type_note: kind === 'other' ? type.note.trim() || undefined : undefined,
        phone_proof: phoneProof,
        attribution,
      }),
    )
    forgetAttribution()
    await navigate('/')
  }

  async function sendCode() {
    const sent = await endpoints.otpRequest(f.phone, inviteRequired ? f.invite_code : undefined)
    setCode('')
    setNow(Date.now())
    setOtp({ resendAt: Date.now() + sent.resend_after * 1000 })
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setConfirmTouched(true)
    setTypeMissing(type.type === '')
    if (confirm !== f.password || type.type === '') return
    void run(async () => {
      if (proof && proof.phone === f.phone) return finish(proof.value)
      try {
        await sendCode()
      } catch (err) {
        // The server has phone verification switched off: sign up as before.
        if (err instanceof ApiError && err.code === 'otp_disabled') return finish()
        throw err
      }
    })
  }

  function verify(e: FormEvent) {
    e.preventDefault()
    void run(async () => {
      const verified = await endpoints.otpVerify(f.phone, code.trim())
      setProof({ phone: f.phone, value: verified.phone_proof })
      await finish(verified.phone_proof)
    })
  }

  if (otp) {
    return (
      <AuthShell title={t.otpTitle}>
        <form onSubmit={verify}>
          <p>{t.otpSent(f.phone)}</p>
          <label>
            {t.otpCode}
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]{6}"
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </label>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <button type="submit" disabled={busy || code.length !== 6}>
            {t.otpVerifyButton}
          </button>
          <button type="button" className="link" disabled={busy || wait > 0} onClick={() => void run(sendCode)}>
            {wait > 0 ? t.otpResendIn(wait) : t.otpResend}
          </button>
          <button type="button" className="link" onClick={() => setOtp(null)}>
            {t.otpChangeData}
          </button>
        </form>
      </AuthShell>
    )
  }

  return (
    <AuthShell title={t.signupTitle}>
      <form onSubmit={submit}>
        {inviteRequired && (
          <label>
            {t.inviteCode}
            <input autoCapitalize="characters" maxLength={64} {...field('invite_code')} />
          </label>
        )}
        <label>
          {t.ownerName}
          <input autoComplete="name" maxLength={80} {...field('name')} />
        </label>
        <label>
          {t.phone}
          <input inputMode="tel" autoComplete="tel" maxLength={20} {...field('phone')} />
        </label>
        <PasswordField
          label={t.newPasswordLabel}
          autoComplete="new-password"
          minLength={8}
          value={f.password}
          onChange={(v) => setF({ ...f, password: v })}
        />
        <PasswordField
          label={t.confirmPassword}
          autoComplete="new-password"
          value={confirm}
          onChange={(v) => {
            setConfirm(v)
            if (v.length >= f.password.length) setConfirmTouched(true)
          }}
          error={mismatch ? t.passwordMismatch : null}
        />
        <label>
          {t.businessName}
          <input maxLength={100} {...field('business_name')} />
        </label>
        <label>
          {t.outletName}
          <input maxLength={100} {...field('outlet_name')} />
        </label>
        <label>
          {t.outletCode}
          <input autoCapitalize="characters" pattern="[A-Za-z0-9]{3,8}" maxLength={8} {...field('outlet_code')} />
        </label>
        <BusinessTypePicker value={type} onChange={setType} missing={typeMissing} />
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {errorCode === 'account_closed' && (
          <p>
            <Link to="/buka-kembali" state={{ phone: f.phone }}>
              {t.reopenLink}
            </Link>
          </p>
        )}
        <button type="submit" disabled={busy}>
          {t.signupButton}
        </button>
      </form>
      <Link to="/masuk">{t.haveAccount}</Link>
    </AuthShell>
  )
}

export function ChangePasswordPage() {
  const session = useSession()
  const { error, busy, run } = useSubmit()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [confirmTouched, setConfirmTouched] = useState(false)
  const mismatch = confirmTouched && confirm !== next

  function submit(e: FormEvent) {
    e.preventDefault()
    setConfirmTouched(true)
    if (confirm !== next) return
    void run(async () => {
      const out = await endpoints.changePassword(current, next)
      session.signedIn({ access_token: out.access_token, expires_in: 900, password_must_change: false })
    })
  }

  return (
    <AuthShell title={t.changePasswordTitle}>
      <p>{t.changePasswordBody}</p>
      <form onSubmit={submit}>
        <PasswordField label={t.currentPassword} autoComplete="current-password" value={current} onChange={setCurrent} />
        <PasswordField label={t.newPassword} autoComplete="new-password" minLength={8} value={next} onChange={setNext} />
        <PasswordField
          label={t.confirmPassword}
          autoComplete="new-password"
          value={confirm}
          onChange={(v) => {
            setConfirm(v)
            if (v.length >= next.length) setConfirmTouched(true)
          }}
          error={mismatch ? t.passwordMismatch : null}
        />
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy}>
          {t.save}
        </button>
      </form>
    </AuthShell>
  )
}
